/*
 * Collagen page interactive components.
 * Self-contained replacement for the GemPages runtime scripts
 * (accordions, carousels, gallery, videos, image comparison,
 * marquee, sticky add-to-cart). No external dependencies.
 */
(() => {
  if (window.__collagenComponents) return;
  window.__collagenComponents = true;

  const ready = (fn) =>
    document.readyState === 'loading'
      ? document.addEventListener('DOMContentLoaded', fn)
      : fn();

  const gpData = (el) => {
    try {
      return JSON.parse(el.getAttribute('gp-data') || '{}');
    } catch {
      return {};
    }
  };

  /* Shared styles for scroll-based sliders and marquee animation. */
  const injectStyles = () => {
    const css = `
      .gps .gem-slider{overflow-x:auto;scroll-snap-type:x mandatory;scroll-behavior:smooth;scrollbar-width:none}
      .gps .gem-slider::-webkit-scrollbar{display:none}
      .gps .gem-slider-item{scroll-snap-align:start}
      .gps gp-carousel-v3 [id^="gp-carousel-"]{overflow-x:auto!important;position:relative;scroll-snap-type:x mandatory;scroll-behavior:smooth;scrollbar-width:none}
      .gps gp-carousel-v3 [id^="gp-carousel-"]::-webkit-scrollbar{display:none}
      .gps .rfm-marquee-container{overflow:hidden}
      .gps gp-lite-youtube-embed{cursor:pointer}
      .gps .gp-accordion-item_body-inner{padding-top:14px}
    `;
    const tag = document.createElement('style');
    tag.textContent = css;
    document.head.appendChild(tag);
  };

  /* Accordions: .gp-accordion-item_body animates via grid-template-rows 0fr -> 1fr. */
  const initAccordions = () => {
    document.querySelectorAll('gp-accordion').forEach((root) => {
      const setting = gpData(root).setting || {};
      const iconOpen = setting.iconExpand;
      const iconClosed = setting.iconCollapse;
      const items = root.querySelectorAll('.gp-accordion-item');

      const setState = (item, open) => {
        const body = item.querySelector('.gp-accordion-item_body');
        const icon = item.querySelector('.gp-collapsible-icon');
        if (!body) return;
        body.dataset.show = String(open);
        body.style.gridTemplateRows = open ? '1fr' : '0fr';
        if (icon && iconOpen && iconClosed) icon.innerHTML = open ? iconOpen : iconClosed;
      };

      items.forEach((item) => {
        const header = item.querySelector('.gp-accordion-item_header');
        if (!header) return;
        header.addEventListener('click', () => {
          const isOpen = item.querySelector('.gp-accordion-item_body')?.dataset.show === 'true';
          items.forEach((other) => setState(other, false));
          if (!isOpen) setState(item, true);
        });
      });
    });
  };

  /* Carousels: native horizontal scroll + arrow buttons; the buy-box gallery
     additionally syncs its thumbnail strip with the main slider. */
  const initCarousels = () => {
    const own = (root, selector) =>
      [...root.querySelectorAll(selector)].filter(
        (el) => el.closest('gp-carousel, gp-carousel-v3') === root);

    document.querySelectorAll('gp-carousel, gp-carousel-v3').forEach((root) => {
      /* v1 scrolls .gem-slider itself; v3 keeps items in .gem-slider-wrapper
         and scrolls the wrapper's overflow:hidden parent div. */
      const track = own(root, '.gem-slider')[0] || own(root, '.gem-slider-wrapper')[0];
      if (!track) return;
      const slider = track.classList.contains('gem-slider-wrapper') ? track.parentElement : track;
      const items = () => [...track.children].filter((c) => c.classList.contains('gem-slider-item'));

      const currentIndex = () => {
        const list = items();
        let best = 0;
        let bestDist = Infinity;
        list.forEach((item, i) => {
          const d = Math.abs(item.offsetLeft - slider.scrollLeft);
          if (d < bestDist) { bestDist = d; best = i; }
        });
        return best;
      };
      const goTo = (i) => {
        const list = items();
        const target = list[Math.max(0, Math.min(i, list.length - 1))];
        if (target) slider.scrollTo({ left: target.offsetLeft, behavior: 'smooth' });
      };

      own(root, 'button[aria-label*="Carousel"]').forEach((btn) => {
        btn.style.setProperty('--v', 'visible');
        const back = /back|prev/i.test(btn.getAttribute('aria-label') || '');
        btn.addEventListener('click', () => goTo(currentIndex() + (back ? -1 : 1)));
      });

      root.__goTo = goTo;
      root.__currentIndex = currentIndex;
    });

    /* Gallery <-> thumbnails sync (ids: gp-carousel-<uid> main, gp-gallery-<uid> thumbs). */
    document.querySelectorAll('gp-carousel').forEach((thumbsRoot) => {
      const id = (gpData(thumbsRoot).id || '');
      if (!id.startsWith('gp-gallery-')) return;
      const uid = id.replace('gp-gallery-', '').split('-')[0];
      const mainRoot = [...document.querySelectorAll('gp-carousel')].find((c) =>
        (gpData(c).id || '').includes(`gp-carousel-${uid}`));
      if (!mainRoot) return;

      const mainSlider = mainRoot.querySelector('.gem-slider');
      const thumbs = [...thumbsRoot.querySelectorAll('.gem-slider-item')];

      const setActive = (index) => {
        thumbs.forEach((t, i) => {
          t.dataset.outline = i === index ? 'active' : 'deactive';
          t.classList.toggle('active', i === index);
        });
      };

      thumbs.forEach((thumb, i) => {
        thumb.addEventListener('click', () => {
          mainRoot.__goTo?.(i);
          setActive(i);
        });
      });

      let scrollTimer;
      mainSlider?.addEventListener('scroll', () => {
        clearTimeout(scrollTimer);
        scrollTimer = setTimeout(() => setActive(mainRoot.__currentIndex?.() ?? 0), 80);
      });
    });
  };

  /* Lazy videos: the snapshot keeps the source in data-src. preload="none"
     means assigning src costs nothing until the user hits play. */
  const initVideos = () => {
    document.querySelectorAll('video[data-src]:not([src])').forEach((video) => {
      video.src = video.dataset.src;
    });
  };

  /* YouTube embeds: swap the thumbnail for an iframe on click. */
  const initYoutube = () => {
    document.querySelectorAll('gp-lite-youtube-embed').forEach((root) => {
      root.addEventListener('click', () => {
        if (root.querySelector('iframe')) return;
        const src = (gpData(root).iframeSrc || '')
          .replace('autoplay=false', 'autoplay=1')
          .replace('controls=0', 'controls=1');
        if (!src) return;
        const iframe = document.createElement('iframe');
        iframe.src = src;
        iframe.allow = 'autoplay; encrypted-media; picture-in-picture';
        iframe.allowFullscreen = true;
        iframe.style.cssText = 'position:absolute;inset:0;width:100%;height:100%;border:0';
        const article = root.querySelector('article') || root;
        article.style.position = 'relative';
        article.appendChild(iframe);
      }, { once: false });
    });
  };

  /* Before/after comparison: drag to clip the left image. */
  const initComparisons = () => {
    document.querySelectorAll('gp-image-comparison').forEach((root) => {
      const container = root.querySelector('[data-testid="container"]');
      const left = root.querySelector('[data-testid="left-image"]');
      if (!container || !left) return;
      const handle = root.querySelector('[data-testid="handle"], [class*="handle"]');

      const setPct = (pct) => {
        const p = Math.max(0, Math.min(100, pct));
        left.style.clipPath = `inset(0 ${100 - p}% 0 0)`;
        if (handle) handle.style.left = `${p}%`;
      };
      setPct(50);

      let dragging = false;
      const move = (e) => {
        if (!dragging) return;
        const rect = container.getBoundingClientRect();
        setPct(((e.clientX - rect.left) / rect.width) * 100);
      };
      container.addEventListener('pointerdown', (e) => {
        dragging = true;
        container.setPointerCapture(e.pointerId);
        move(e);
      });
      container.addEventListener('pointermove', move);
      container.addEventListener('pointerup', () => { dragging = false; });
      container.style.touchAction = 'pan-y';
    });
  };

  /* Simple thumbnail galleries ([data-cgc]): thumbs and arrows switch the
     active slide; the active thumb is kept scrolled into view inside its rail. */
  const initSimpleGalleries = () => {
    document.querySelectorAll('[data-cgc]').forEach((root) => {
      const slides = [...root.querySelectorAll('[data-cgc-slide]')];
      const thumbs = [...root.querySelectorAll('[data-cgc-thumb]')];
      const rail = root.querySelector('.cgc__thumbs');
      if (!slides.length) return;
      let index = 0;

      const setIndex = (i) => {
        index = (i + slides.length) % slides.length;
        slides.forEach((s, n) => s.classList.toggle('is-active', n === index));
        thumbs.forEach((t, n) => t.classList.toggle('is-active', n === index));
        const active = thumbs[index];
        if (rail && active) {
          rail.scrollTo({ top: active.offsetTop - rail.offsetTop, left: active.offsetLeft - rail.offsetLeft });
        }
      };

      thumbs.forEach((t, n) => t.addEventListener('click', () => setIndex(n)));
      root.querySelectorAll('[data-cgc-step]').forEach((btn) =>
        btn.addEventListener('click', () => setIndex(index + Number(btn.dataset.cgcStep))));
    });
  };

  /* Image strip carousels ([data-cir]): scroll-snap track, arrows, dots,
     optional autoplay (data-cir-autoplay="seconds", pauses on hover). */
  const initImageRows = () => {
    document.querySelectorAll('[data-cir]').forEach((root) => {
      const track = root.querySelector('[data-cir-track]');
      if (!track) return;
      const items = [...track.children];
      if (!items.length) return;
      const dotsWrap = root.querySelector('[data-cir-dots]');
      let index = 0;

      const dots = items.map((_, i) => {
        if (!dotsWrap) return null;
        const dot = document.createElement('button');
        dot.type = 'button';
        dot.className = 'c-imgrow__dot';
        dot.setAttribute('aria-label', `Go to image ${i + 1}`);
        dot.addEventListener('click', () => go(i));
        dotsWrap.appendChild(dot);
        return dot;
      });
      const setActive = () => dots.forEach((d, i) => d?.classList.toggle('is-active', i === index));

      const go = (i) => {
        index = (i + items.length) % items.length;
        track.scrollTo({ left: items[index].offsetLeft - track.offsetLeft, behavior: 'smooth' });
        setActive();
      };
      root.querySelector('[data-cir-prev]')?.addEventListener('click', () => go(index - 1));
      root.querySelector('[data-cir-next]')?.addEventListener('click', () => go(index + 1));

      let scrollTimer;
      track.addEventListener('scroll', () => {
        clearTimeout(scrollTimer);
        scrollTimer = setTimeout(() => {
          let best = 0;
          let bestDist = Infinity;
          items.forEach((item, i) => {
            const d = Math.abs(item.offsetLeft - track.offsetLeft - track.scrollLeft);
            if (d < bestDist) { bestDist = d; best = i; }
          });
          index = best;
          setActive();
        }, 80);
      });

      const seconds = Number(root.dataset.cirAutoplay || 0);
      let timer;
      const play = () => { if (seconds) timer = setInterval(() => go(index + 1), seconds * 1000); };
      const pause = () => clearInterval(timer);
      root.addEventListener('pointerenter', pause);
      root.addEventListener('pointerleave', play);
      setActive();
      play();
    });
  };

  /* Marquees: gp-base CSS already defines the gp-merquee-scroll animation but
     expects the runtime to fill the empty placeholder strip and set --duration. */
  const initMarquees = () => {
    document.querySelectorAll('gp-marquee').forEach((root) => {
      const setting = gpData(root).setting || {};
      const marquees = [...root.querySelectorAll('.rfm-marquee')];
      const primary = marquees.find((m) => m.children.length);
      if (!primary) return;
      marquees.filter((m) => !m.children.length).forEach((placeholder) => {
        placeholder.innerHTML = primary.innerHTML;
      });

      const pxPerSec = 50 * (Number(setting.speed) || 1);
      const apply = () => {
        const duration = Math.max(5, Math.round(primary.scrollWidth / pxPerSec));
        marquees.forEach((m) => {
          m.style.setProperty('--duration', `${duration}s`);
          m.style.setProperty('--pause-on-hover', setting.stopOnHover ? 'paused' : 'running');
          m.style.setProperty('--min-width', '100%');
        });
      };
      apply();
      window.addEventListener('load', apply);
      window.addEventListener('resize', apply);
    });
  };

  /* Sticky add-to-cart: show once the buy-box cart button scrolls out of view. */
  const initSticky = () => {
    const wrapper = document.querySelector('.sticky-wrapper');
    const target = document.querySelector('form[action*="/cart/add"] button[name="add"], button[name="add"]');
    if (!wrapper || !target || !('IntersectionObserver' in window)) return;

    const setVisible = (visible) => {
      ['--d', '--d-tablet', '--d-mobile'].forEach((key) =>
        wrapper.style.setProperty(key, visible ? 'block' : 'none'));
    };
    setVisible(false);

    new IntersectionObserver(([entry]) => {
      setVisible(!entry.isIntersecting && entry.boundingClientRect.bottom < 0);
    }).observe(target);
  };

  ready(() => {
    injectStyles();
    initAccordions();
    initCarousels();
    initVideos();
    initYoutube();
    initComparisons();
    initSimpleGalleries();
    initImageRows();
    initMarquees();
    initSticky();
  });
})();
